import {
  STATUS_TABS,
  chapterState,
  countsByTab,
  ENDORSE_LABELS,
  fmtDate,
  isFullyEndorsed,
  panelOf,
  refereeCell,
  statusLabel,
  tabLabel,
  type StatusKey,
} from "./applicationStatus";

/**
 * Exports of the entry (probationer) applications list, in the two forms the
 * secretariat actually files things in: a spreadsheet to work from, and a
 * document to sign and put in front of a committee.
 *
 * The entry-side twin of ../../upgrade-application/utils/exportUpgrades.ts, and
 * deliberately the same document: the same committee reads both, and two
 * listings of the same sitting that are laid out differently invite the reading
 * that they are about different things.
 *
 * Both are built from the SAME rows the table is showing and filed by the same
 * panelOf() the tabs use, so an export can never disagree with the screen it was
 * taken from. Each carries a header naming the diet, the category, the panel and
 * the filter, plus the full status breakdown — a listing with no statement of
 * what it is a listing OF is worthless a week later.
 *
 * Dependency-free for the same reasons as the upgrade exports: the spreadsheet
 * is CSV with a UTF-8 BOM, which Excel opens directly and which renders accented
 * names correctly; the PDF is a styled print window where the browser's own
 * "Save as PDF" does the writing.
 *
 * No photograph column, unlike the upgrade export. That one exists to build
 * induction paperwork from faces; an entry applicant has not been admitted to
 * anything yet, and printing a page of photographs of people the committee has
 * not decided on is not something this listing is for.
 */

export type ExportMeta = {
  /** The category applied for, e.g. "Probationer". */
  category: string;
  /** The diet these rows were submitted into. */
  diet?: string;
  /** Which panel was open when the export was taken. */
  tab: StatusKey;
  /** The search box's contents, if any. */
  query?: string;
};

/** The document's own title. */
function heading(meta: ExportMeta): string {
  return `Entry Applications — ${meta.category}`;
}

/** One row of the export, in the order the columns are written. */
const COLUMNS = [
  "Applicant",
  "Membership No",
  "Email",
  "Phone",
  "Category",
  "Chapter",
  "Chapter Endorsement",
  "Referee 1",
  "Referee 1 Decision",
  "Referee 2",
  "Referee 2 Decision",
  "Fully Endorsed",
  "Reviewer",
  "Status",
  "Filed Under",
  "Submitted",
] as const;

function applicantName(r: any): string {
  return (
    [r?.title, r?.surname, r?.other_names].filter(Boolean).join(" ") ||
    r?.user?.name ||
    "—"
  );
}

function reviewerName(r: any): string {
  if (!r?.reviewer) return "";
  return (
    [r.reviewer.surname, r.reviewer.firstname].filter(Boolean).join(" ") ||
    r.reviewer.membership_no ||
    ""
  );
}

function toRow(r: any): string[] {
  const ref1 = refereeCell(r, 0);
  const ref2 = refereeCell(r, 1);

  return [
    applicantName(r),
    r?.membership_no ?? "",
    r?.user?.email ?? r?.email ?? "",
    r?.phone ?? r?.phone_number ?? "",
    r?.category?.name ?? "",
    r?.chapter?.name ?? r?.chapter_other ?? "",
    ENDORSE_LABELS[chapterState(r)],
    ref1.name ?? "",
    ref1.name ? ENDORSE_LABELS[ref1.state] : "",
    ref2.name ?? "",
    ref2.name ? ENDORSE_LABELS[ref2.state] : "",
    isFullyEndorsed(r) ? "Yes" : "No",
    reviewerName(r),
    statusLabel(r?.status),
    tabLabel(panelOf(r)),
    fmtDate(r?.created_at),
  ];
}

/** Today, for the "taken on" line and the filename. */
function stamp() {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    printed: now.toLocaleString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }),
    file: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
  };
}

function slug(text: string) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "export";
}

function filename(meta: ExportMeta, extension: string) {
  const parts = [
    "entry-applications",
    meta.diet ? slug(meta.diet) : null,
    slug(meta.category),
    slug(tabLabel(meta.tab)),
    stamp().file,
  ].filter(Boolean);
  return `${parts.join("-")}.${extension}`;
}

/** Every status and its count, for the report block both exports carry. */
function breakdown(allRows: any[]) {
  const counts = countsByTab(allRows);
  return STATUS_TABS.map((t) => ({ label: t.label, count: counts.get(t.key) || 0 }));
}

/* ── Spreadsheet ─────────────────────────────────────────────────────────── */

/** RFC 4180: quote everything, and double any quote inside a field. */
const csvCell = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const csvLine = (cells: unknown[]) => cells.map(csvCell).join(",");

export function exportApplicationsSpreadsheet(
  rows: any[],
  allRows: any[],
  meta: ExportMeta,
) {
  const { printed } = stamp();

  const lines: string[] = [
    csvLine([`NIQS — ${heading(meta)}`]),
    ...(meta.diet ? [csvLine([`Diet: ${meta.diet}`])] : []),
    csvLine([`Panel: ${tabLabel(meta.tab)}`]),
    ...(meta.query?.trim() ? [csvLine([`Search: ${meta.query.trim()}`])] : []),
    csvLine([`Applications listed: ${rows.length}`]),
    csvLine([`Taken on: ${printed}`]),
    "",
    csvLine(["Status breakdown (whole category)"]),
    ...breakdown(allRows).map((b) => csvLine([b.label, b.count])),
    "",
    csvLine([...COLUMNS]),
    ...rows.map((r) => csvLine(toRow(r))),
  ];

  // The BOM is what makes Excel read this as UTF-8 rather than the local
  // codepage — without it any accented name arrives as mojibake.
  const blob = new Blob(["﻿" + lines.join("\r\n")], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename(meta, "csv");
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick — Safari cancels the download if the URL dies
  // before it has read it.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ── PDF (via the browser's print dialog) ────────────────────────────────── */

const esc = (v: unknown) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export function exportApplicationsPdf(rows: any[], allRows: any[], meta: ExportMeta) {
  const { printed } = stamp();
  const w = window.open("", "_blank", "width=1100,height=800");
  if (!w) {
    alert("Allow pop-ups for this site to export a PDF.");
    return;
  }

  const summary = breakdown(allRows)
    .map(
      (b) =>
        `<div class="stat"><span class="n">${b.count}</span><span class="l">${esc(
          b.label,
        )}</span></div>`,
    )
    .join("");

  const body = rows.length
    ? rows
        .map((r) => `<tr>${toRow(r).map((c) => `<td>${esc(c)}</td>`).join("")}</tr>`)
        .join("")
    : `<tr><td class="empty" colspan="${COLUMNS.length}">No applications in this panel.</td></tr>`;

  w.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${esc(filename(meta, "pdf").replace(/\.pdf$/, ""))}</title>
  <style>
    @page{size:A4 landscape;margin:12mm}
    *{box-sizing:border-box;margin:0;padding:0;font-family:'Segoe UI',Arial,sans-serif}
    body{color:#111;font-size:10px}
    .head{border-bottom:2px solid #111;padding-bottom:12px;margin-bottom:14px}
    .org{font-size:9px;font-weight:900;letter-spacing:.22em;text-transform:uppercase;color:#888}
    h1{font-size:17px;font-weight:900;margin-top:4px}
    .sub{font-size:10px;color:#666;margin-top:5px;line-height:1.6}
    .sub b{color:#111}
    .stats{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:14px}
    .stat{border:1px solid #e6e6e6;border-radius:7px;padding:6px 10px;min-width:78px}
    .stat .n{display:block;font-size:15px;font-weight:900}
    .stat .l{display:block;font-size:8px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#999;margin-top:1px}
    table{width:100%;border-collapse:collapse}
    th{background:#111;color:#fff;font-size:8px;font-weight:900;letter-spacing:.08em;text-transform:uppercase;text-align:left;padding:7px 6px}
    td{padding:6px;border-bottom:1px solid #eee;vertical-align:top}
    td.empty{text-align:center;color:#999;padding:24px}
    tr{page-break-inside:avoid}
    thead{display:table-header-group}
    .foot{margin-top:14px;padding-top:8px;border-top:1px solid #eee;font-size:8px;color:#aaa;font-weight:800;letter-spacing:.12em;text-transform:uppercase}
  </style>
</head>
<body>
  <div class="head">
    <div class="org">Nigerian Institute of Quantity Surveyors</div>
    <h1>${esc(heading(meta))}</h1>
    <div class="sub">
      ${meta.diet ? `Diet: <b>${esc(meta.diet)}</b> &middot; ` : ""}Panel: <b>${esc(
        tabLabel(meta.tab),
      )}</b> &middot; ${rows.length} application(s) listed${
        meta.query?.trim() ? ` &middot; filtered by &ldquo;${esc(meta.query.trim())}&rdquo;` : ""
      }<br>
      Taken on ${esc(printed)}
    </div>
  </div>

  <div class="stats">${summary}</div>

  <table>
    <thead><tr>${COLUMNS.map((c) => `<th>${esc(c)}</th>`).join("")}</tr></thead>
    <tbody>${body}</tbody>
  </table>

  <div class="foot">NIQS Portal &middot; Entry Applications Report</div>
  <script>
    (function () {
      var printed = false;
      function go() { if (printed) return; printed = true; window.print(); }
      window.onload = go;
      setTimeout(go, 6000);
    })();
  </script>
</body>
</html>`);
  w.document.close();
}
